import React, { useContext, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faUser,
  faChevronDown,
  faCaretDown,
} from "@fortawesome/free-solid-svg-icons";
import { Link, useLocation } from "react-router-dom";
import UserContext from "../../contexts/UserContext";
import { useMediaQuery } from "react-responsive";
import { baseUrl } from "../../config";
import { trackEvent } from "../../components/trackingUtils";
import "./Header.css"; // You can remove this if you no longer need custom CSS

const Header = ({ blogs }) => {
  const { user, isAuthenticated, logout } = useContext(UserContext);
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const isTabletOrMobile = useMediaQuery({ maxWidth: 1023 });
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleOffcanvas = () => {
    setShowOffcanvas(!showOffcanvas);
  };

  const handleDropdownClick = (name) => {
    setOpenDropdown((prev) => (prev === name ? null : name));
  };

  const linkColorClasses =
    isHome && !isScrolled
      ? "text-gray-200 hover:text-white"
      : "text-gray-300 hover:text-white";

  // Desktop dropdown control (prevents flicker on hover gaps)
  const handleDesktopEnter = (name) => {
    if (!isTabletOrMobile) setOpenDropdown(name);
  };
  const handleDesktopLeave = () => {
    if (!isTabletOrMobile) setOpenDropdown(null);
  };

  return (
    <>
      <nav
        className={`fixed w-full z-50 transition-all duration-300 ${
          isHome && !isScrolled
            ? "bg-gradient-to-br from-[#0b1220] via-[#142032] to-[#0b1220]"
            : "bg-[#0b1220]/95 backdrop-blur-lg"
        } ${isScrolled ? "shadow-xl" : "shadow-none"}`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center py-3 sm:py-4">
          <Link to="/" className="flex items-center z-50">
            <img
              src={baseUrl + `/images/logo.svg`}
              className="h-10 sm:h-10 md:h-12 lg:h-14 transition-transform duration-300 hover:scale-110"
              alt="GeniusNexa Logo"
            />
          </Link>
          <button
            className="text-white focus:outline-none lg:hidden p-2.5 hover:bg-white/10 rounded-lg transition-all z-50 active:scale-95"
            onClick={toggleOffcanvas}
            aria-label="Toggle menu"
          >
            <FontAwesomeIcon icon={faBars} className="text-xl sm:text-2xl" />
          </button>
          <div
            className={`lg:flex items-center ${
              isTabletOrMobile ? "hidden" : ""
            }`}
          >
            <Link
              to="/services"
              className={`nav-link menu-item px-3 lg:px-4 py-2 font-medium transition-colors relative group text-sm lg:text-base ${linkColorClasses}`}
              onClick={() => trackEvent("Navigation", "Services", "Header")}
            >
              Services
              <span className="absolute bottom-0 left-3 lg:left-4 right-3 lg:right-4 h-0.5 bg-[#E5C558] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
            </Link>
            <div
              className="relative"
              onMouseEnter={() => handleDesktopEnter("blogs")}
              onMouseLeave={handleDesktopLeave}
            >
              <button
                className={`nav-link menu-item px-3 lg:px-4 py-2 flex items-center font-medium transition-colors text-sm lg:text-base ${linkColorClasses}`}
              >
                Blogs
                <FontAwesomeIcon
                  icon={faCaretDown}
                  className={`ml-1.5 lg:ml-2 text-xs lg:text-sm transition-transform duration-300 ${
                    openDropdown === "blogs" ? "rotate-180" : ""
                  }`}
                />
              </button>
              <div
                className={`absolute left-0 top-full bg-white dark:bg-[#121e2e] shadow-xl rounded-lg w-56 py-2 border border-gray-200 dark:border-white/10 z-50 animate-fadeIn ${
                  openDropdown === "blogs" ? "block" : "hidden"
                }`}
              >
                {blogs.map((blog, index) => (
                  <React.Fragment key={blog.SEOUrl}>
                    <Link
                      to={`/blogs/${blog.SEOUrl}`}
                      className="block px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-[#E5C558]/10 hover:text-[#0b1220] dark:hover:text-[#E5C558] transition-colors"
                      onClick={() => {
                        setOpenDropdown(null);
                        trackEvent(
                          "Navigation",
                          `Blog_${blog.Title}`,
                          "Header",
                        );
                      }}
                    >
                      {blog.Title}
                    </Link>
                    {index < blogs.length - 1 && (
                      <div className="border-t border-gray-200 dark:border-white/10"></div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <Link
              to="/contact-us"
              className={`nav-link menu-item px-3 lg:px-4 py-2 font-medium transition-colors relative group text-sm lg:text-base ${linkColorClasses}`}
              onClick={() => trackEvent("Navigation", "ContactUs", "Header")}
            >
              Contact Us
              <span className="absolute bottom-0 left-3 lg:left-4 right-3 lg:right-4 h-0.5 bg-[#E5C558] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
            </Link>
            <div
              className="relative"
              onMouseEnter={() => handleDesktopEnter("tools")}
              onMouseLeave={handleDesktopLeave}
            >
              <button
                className={`nav-link menu-item px-3 lg:px-4 py-2 flex items-center font-medium transition-colors text-sm lg:text-base ${linkColorClasses}`}
              >
                Tools
                <FontAwesomeIcon
                  icon={faCaretDown}
                  className={`ml-1.5 lg:ml-2 text-xs lg:text-sm transition-transform duration-300 ${
                    openDropdown === "tools" ? "rotate-180" : ""
                  }`}
                />
              </button>
              <div
                className={`absolute left-0 top-full bg-white dark:bg-[#121e2e] shadow-xl rounded-lg w-56 py-2 border border-gray-200 dark:border-white/10 z-50 animate-fadeIn ${
                  openDropdown === "tools" ? "block" : "hidden"
                }`}
              >
                <Link
                  to={`/tools/tableiq`}
                  className="block px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-[#E5C558]/10 hover:text-[#0b1220] dark:hover:text-[#E5C558] transition-colors"
                  onClick={() => {
                    setOpenDropdown(null);
                    trackEvent("Navigation", "MetaGenAI", "Header");
                  }}
                >
                  MetaGen AI
                </Link>
                <div className="border-t border-gray-200 dark:border-white/10"></div>
                <Link
                  to={`/tools/summarai`}
                  className="block px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-[#E5C558]/10 hover:text-[#0b1220] dark:hover:text-[#E5C558] transition-colors"
                  onClick={() => {
                    setOpenDropdown(null);
                    trackEvent("Navigation", "SummarizeAI", "Header");
                  }}
                >
                  Summarize AI
                </Link>
                <div className="border-t border-gray-200 dark:border-white/10"></div>
                <Link
                  to={`/tools/comparexpert`}
                  className="block px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-[#E5C558]/10 hover:text-[#0b1220] dark:hover:text-[#E5C558] transition-colors"
                  onClick={() => setOpenDropdown(null)}
                >
                  CompareXpert
                </Link>
                <div className="border-t border-gray-200 dark:border-white/10"></div>
                <Link
                  to={`/tools/seonavigator`}
                  className="block px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-[#E5C558]/10 hover:text-[#0b1220] dark:hover:text-[#E5C558] transition-colors"
                  onClick={() => setOpenDropdown(null)}
                >
                  SEO Navigator
                </Link>
                <div className="border-t border-gray-200 dark:border-white/10"></div>
                <Link
                  to={`/tools/imageconverter`}
                  className="block px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-[#E5C558]/10 hover:text-[#0b1220] dark:hover:text-[#E5C558] transition-colors"
                  onClick={() => setOpenDropdown(null)}
                >
                  Image Converter
                </Link>
              </div>
            </div>
            {user && user.role === "admin" && (
              <div
                className="relative"
                onMouseEnter={() => handleDesktopEnter("admin-menu")}
                onMouseLeave={handleDesktopLeave}
              >
                <button
                  className={`nav-link menu-item px-3 lg:px-4 py-2 flex items-center font-medium transition-colors text-sm lg:text-base ${linkColorClasses}`}
                >
                  Admin
                  <FontAwesomeIcon
                    icon={faCaretDown}
                    className={`ml-1.5 lg:ml-2 text-xs lg:text-sm transition-transform duration-300 ${
                      openDropdown === "admin-menu" ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <div
                  className={`absolute left-0 top-full bg-white dark:bg-[#121e2e] shadow-xl rounded-lg w-56 py-2 border border-gray-200 dark:border-white/10 z-50 animate-fadeIn ${
                    openDropdown === "admin-menu" ? "block" : "hidden"
                  }`}
                >
                  <Link
                    to="/admin"
                    className="block px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-[#E5C558]/10 hover:text-[#0b1220] dark:hover:text-[#E5C558] transition-colors"
                    onClick={() => setOpenDropdown(null)}
                  >
                    Dashboard
                  </Link>
                  <div className="border-t border-gray-200 dark:border-white/10"></div>
                  <Link
                    to="/bank/Budget"
                    className="block px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-[#E5C558]/10 hover:text-[#0b1220] dark:hover:text-[#E5C558] transition-colors"
                    onClick={() => setOpenDropdown(null)}
                  >
                    Budget
                  </Link>
                  <div className="border-t border-gray-200 dark:border-white/10"></div>
                  <Link
                    to="/bills"
                    className="block px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-[#E5C558]/10 hover:text-[#0b1220] dark:hover:text-[#E5C558] transition-colors"
                    onClick={() => setOpenDropdown(null)}
                  >
                    Bills
                  </Link>
                  <div className="border-t border-gray-200 dark:border-white/10"></div>
                  <Link
                    to="/study-hub"
                    className="block px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-[#E5C558]/10 hover:text-[#0b1220] dark:hover:text-[#E5C558] transition-colors"
                    onClick={() => setOpenDropdown(null)}
                  >
                    Study Hub
                  </Link>
                </div>
              </div>
            )}
            {isAuthenticated ? (
              <div
                className="relative"
                onMouseEnter={() => handleDesktopEnter("user")}
                onMouseLeave={handleDesktopLeave}
              >
                <div className="nav-link menu-item px-4 flex items-center cursor-pointer">
                  <div className="user-avatar-container">
                    {user && user.userImage ? (
                      <img
                        src={user.userImage}
                        alt="User Avatar"
                        className="user-avatar h-8 w-8 rounded-full ring-2 ring-[#E5C558] hover:ring-4 transition-all"
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faUser}
                        className="user-icon text-gray-300"
                      />
                    )}
                  </div>
                </div>
                <div
                  className={`absolute top-full bg-[#0f1826] shadow-xl rounded-lg w-48 py-2 border border-white/10 right-0 animate-fadeIn z-50 ${
                    openDropdown === "user" ? "block" : "hidden"
                  }`}
                >
                  <Link
                    to="/settings"
                    className="block px-4 py-3 hover:bg-white/10 text-gray-200 hover:text-[#E5C558] transition-all"
                    onClick={() => setShowOffcanvas(false)}
                  >
                    Settings
                  </Link>
                  <div className="border-t border-gray-100"></div>
                  <button
                    onClick={() => {
                      logout();
                      setShowOffcanvas(false);
                    }}
                    className="block px-4 py-3 w-full text-left hover:bg-white/10 text-gray-200 hover:text-[#E5C558] transition-all"
                  >
                    Logout
                  </button>
                </div>
              </div>
            ) : (
              <Link
                to="/login"
                className="ml-2 lg:ml-4 px-4 lg:px-6 py-2 text-sm lg:text-base bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-[#0b1220] font-semibold rounded-full hover:shadow-lg hover:shadow-[#E5C558]/30 transition-all hover:scale-105 active:scale-95"
                onClick={() => setShowOffcanvas(false)}
              >
                Sign Up
              </Link>
            )}
          </div>
        </div>
      </nav>

      {showOffcanvas && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-fadeIn lg:hidden"
          onClick={toggleOffcanvas}
        >
          <div
            className="fixed inset-y-0 left-0 bg-[#0b1220] w-[85vw] max-w-sm p-6 shadow-2xl overflow-y-auto animate-slideInLeft"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="text-gray-200 focus:outline-none p-3 hover:bg-white/5 rounded-lg mb-6 transition-all active:scale-95"
              onClick={toggleOffcanvas}
              aria-label="Close menu"
            >
              <svg
                className="w-7 h-7"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* User Avatar in Mobile */}
            {isAuthenticated && (
              <div className="mb-6 pb-6 border-b border-white/10">
                <div className="flex items-center space-x-4">
                  {user && user.userImage ? (
                    <img
                      src={user.userImage}
                      alt="User Avatar"
                      className="h-14 w-14 rounded-full ring-2 ring-[#E5C558] object-cover"
                    />
                  ) : (
                    <div className="h-14 w-14 rounded-full bg-[#E5C558]/20 flex items-center justify-center flex-shrink-0">
                      <FontAwesomeIcon
                        icon={faUser}
                        className="h-7 w-7 text-[#E5C558]"
                      />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-gray-200 truncate">
                      {user?.name || "User"}
                    </p>
                    <p className="text-sm text-gray-400 truncate">
                      {user?.email || ""}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <nav className="space-y-1.5">
              <Link
                to="/services"
                className="block px-4 py-3.5 rounded-lg hover:bg-[#E5C558]/10 text-gray-200 hover:text-[#E5C558] font-medium transition-all active:bg-[#E5C558]/20"
                onClick={() => setShowOffcanvas(false)}
              >
                Services
              </Link>

              <div>
                <button
                  className="px-4 py-3.5 w-full text-left rounded-lg hover:bg-[#E5C558]/10 flex items-center justify-between text-gray-200 hover:text-[#E5C558] font-medium transition-all active:bg-[#E5C558]/20"
                  onClick={() => handleDropdownClick("blogs")}
                >
                  Blogs
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`transition-transform duration-300 text-sm ${
                      openDropdown === "blogs" ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openDropdown === "blogs" && (
                  <div className="pl-4 mt-1.5 space-y-1 animate-fadeIn">
                    {blogs.map((blog, index) => (
                      <Link
                        key={blog.SEOUrl}
                        to={`/blogs/${blog.SEOUrl}`}
                        className="block px-4 py-2.5 rounded-lg hover:bg-[#E5C558]/10 text-gray-300 hover:text-[#E5C558] transition-all active:bg-[#E5C558]/20 text-sm"
                        onClick={() => setShowOffcanvas(false)}
                      >
                        {blog.Title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/contact-us"
                className="block px-4 py-3.5 rounded-lg hover:bg-[#E5C558]/10 text-gray-200 hover:text-[#E5C558] font-medium transition-all active:bg-[#E5C558]/20"
                onClick={() => setShowOffcanvas(false)}
              >
                Contact Us
              </Link>

              <div>
                <button
                  className="px-4 py-3.5 w-full text-left rounded-lg hover:bg-[#E5C558]/10 flex items-center justify-between text-gray-200 hover:text-[#E5C558] font-medium transition-all active:bg-[#E5C558]/20"
                  onClick={() => handleDropdownClick("tools")}
                >
                  Tools
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`transition-transform duration-300 text-sm ${
                      openDropdown === "tools" ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openDropdown === "tools" && (
                  <div className="pl-4 mt-1.5 space-y-1 animate-fadeIn">
                    <Link
                      to="/tools/tableiq"
                      className="block px-4 py-2.5 rounded-lg hover:bg-[#E5C558]/10 text-gray-300 hover:text-[#E5C558] transition-all active:bg-[#E5C558]/20 text-sm"
                      onClick={() => setShowOffcanvas(false)}
                    >
                      MetaGen AI
                    </Link>
                    <Link
                      to="/tools/summarai"
                      className="block px-4 py-2.5 rounded-lg hover:bg-[#E5C558]/10 text-gray-300 hover:text-[#E5C558] transition-all active:bg-[#E5C558]/20 text-sm"
                      onClick={() => setShowOffcanvas(false)}
                    >
                      Summarize AI
                    </Link>
                    <Link
                      to="/tools/comparexpert"
                      className="block px-4 py-2.5 rounded-lg hover:bg-[#E5C558]/10 text-gray-300 hover:text-[#E5C558] transition-all active:bg-[#E5C558]/20 text-sm"
                      onClick={() => setShowOffcanvas(false)}
                    >
                      CompareXpert
                    </Link>
                    <Link
                      to="/tools/seonavigator"
                      className="block px-4 py-2.5 rounded-lg hover:bg-[#E5C558]/10 text-gray-300 hover:text-[#E5C558] transition-all active:bg-[#E5C558]/20 text-sm"
                      onClick={() => setShowOffcanvas(false)}
                    >
                      SEO Navigator
                    </Link>
                    <Link
                      to="/tools/imageconverter"
                      className="block px-4 py-2.5 rounded-lg hover:bg-[#E5C558]/10 text-gray-300 hover:text-[#E5C558] transition-all active:bg-[#E5C558]/20 text-sm"
                      onClick={() => setShowOffcanvas(false)}
                    >
                      Image Converter
                    </Link>
                  </div>
                )}
              </div>

              {isAuthenticated ? (
                <>
                  <div className="my-4 border-t border-white/10"></div>
                  <div>
                    <button
                      className="px-4 py-3.5 w-full text-left rounded-lg hover:bg-[#E5C558]/10 flex items-center justify-between text-gray-200 hover:text-[#E5C558] font-medium transition-all active:bg-[#E5C558]/20"
                      onClick={() => handleDropdownClick("admin")}
                    >
                      Admin
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        className={`transition-transform duration-300 text-sm ${
                          openDropdown === "admin" ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {openDropdown === "admin" && (
                      <div className="pl-4 mt-1.5 animate-fadeIn">
                        <Link
                          to="/admin"
                          className="block px-4 py-2.5 rounded-lg hover:bg-[#E5C558]/10 text-gray-300 hover:text-[#E5C558] transition-all active:bg-[#E5C558]/20 text-sm"
                          onClick={() => setShowOffcanvas(false)}
                        >
                          Dashboard
                        </Link>
                        <Link
                          to="/bank/Budget"
                          className="block px-4 py-2.5 rounded-lg hover:bg-[#E5C558]/10 text-gray-300 hover:text-[#E5C558] transition-all active:bg-[#E5C558]/20 text-sm"
                          onClick={() => setShowOffcanvas(false)}
                        >
                          Budget
                        </Link>
                        <Link
                          to="/bills"
                          className="block px-4 py-2.5 rounded-lg hover:bg-[#E5C558]/10 text-gray-300 hover:text-[#E5C558] transition-all active:bg-[#E5C558]/20 text-sm"
                          onClick={() => setShowOffcanvas(false)}
                        >
                          Bills
                        </Link>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      logout();
                      setShowOffcanvas(false);
                    }}
                    className="block px-4 py-3.5 w-full text-left rounded-lg hover:bg-red-600/10 text-red-400 hover:text-red-300 font-medium transition-all active:bg-red-700/10"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <div className="mt-6">
                  <Link
                    to="/login"
                    className="block px-6 py-4 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-[#0b1220] font-semibold rounded-full text-center hover:shadow-lg transition-all active:scale-95 text-base"
                    onClick={() => setShowOffcanvas(false)}
                  >
                    Sign Up
                  </Link>
                </div>
              )}
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
